import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Banner,
  Button,
  MonoLabel,
  Skeleton,
  Tag,
  TextField
} from '../components/primitives';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import {
  SettingsDocumentsTabs,
  type SettingsDocumentsTab
} from '../components/settings/SettingsDocumentsTabs';
import { supabase } from '../lib/supabase';
import { useActiveOrg } from '../lib/activeOrg';
import './SettingsDocuments.css';

interface PropertyDefRow {
  id: string;
  key: string;
  label: string;
  kind: string;
  position: number;
}

interface PropertyOptionRow {
  id: string;
  property_def_id: string;
  key: string;
  label: string;
  position: number;
}

interface DocumentTypeRow {
  id: string;
  key: string;
  name: string;
  built_in: boolean;
}

type LoadState = 'loading' | 'ready' | 'error' | 'unauth' | 'empty';

/**
 * System property defs — the workspace-pool rows that replaced the old
 * hardcoded documents columns (Phases 2/3/4). They render with a "System"
 * badge in the Property library and have label/key/kind locked from edits.
 * (`subtype` is retired in favour of the first-class document Type.)
 */
const SYSTEM_PROPERTY_KEYS = new Set(['owner', 'lifecycle', 'subtype']);
// 'canvas' is a Projects-owned built-in type, hidden from the Types manager.
const HIDDEN_TYPE_KEYS = new Set(['canvas']);

function propertyKey(label: string): string {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);
  return base.length >= 2 ? base : `prop_${Date.now().toString(36).slice(-6)}`;
}

function uniqueKey(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}

/**
 * `/settings/documents` — Types + Property library tabs.
 *
 * Types tab: the org's document_types (the single "Type" field). Members with
 * `documents.create` can add a Type via the `create_document_type` RPC; managers
 * archive user-created Types via `set_document_type_archived`. Built-in Types are
 * protected from archive. The Property library tab holds the custom property
 * definitions (Owner / Lifecycle + user properties).
 */
export function SettingsDocuments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: SettingsDocumentsTab =
    tabParam === 'property-library' ? 'property-library' : 'types';

  // The active org is owned by the shell (see `useActiveOrg`). This page is a
  // pure consumer: it scopes every query/insert to whatever org the shell says
  // is active and re-loads when the user switches. It must NOT re-derive its
  // own "oldest membership" org — that was the cross-org leak.
  const { activeOrgId: orgId } = useActiveOrg();

  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [defaultTypeId, setDefaultTypeId] = useState<string | null>(null);
  const [documentTypes, setDocumentTypes] = useState<
    ReadonlyArray<DocumentTypeRow>
  >([]);
  const [activeTypeId, setActiveTypeId] = useState<string | null>(null);
  const [propertyDefs, setPropertyDefs] = useState<
    ReadonlyArray<PropertyDefRow>
  >([]);
  const [propertyOptions, setPropertyOptions] = useState<
    ReadonlyArray<PropertyOptionRow>
  >([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState('loading');
      setError(null);

      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setState('unauth');
        return;
      }

      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!orgId) return;

      const [defRes, optRes, typeRes] = await Promise.all([
        supabase
          .from('document_property_defs')
          .select('id, key, label, kind, position')
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('position', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase
          .from('document_property_options')
          .select('id, property_def_id, key, label, position')
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('position', { ascending: true }),
        supabase
          .from('document_types')
          .select('id, key, name, built_in')
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('is_default', { ascending: false })
          .order('name', { ascending: true })
      ]);
      if (cancelled) return;
      if (defRes.error || optRes.error || typeRes.error) {
        setError(
          defRes.error?.message ??
            optRes.error?.message ??
            typeRes.error?.message ??
            'Load failed'
        );
        setState('error');
        return;
      }
      setPropertyDefs((defRes.data ?? []) as PropertyDefRow[]);
      setPropertyOptions((optRes.data ?? []) as PropertyOptionRow[]);
      const types = ((typeRes.data ?? []) as DocumentTypeRow[]).filter(
        (t) => !HIDDEN_TYPE_KEYS.has(t.key)
      );
      setDocumentTypes(types);
      setDefaultTypeId(types[0]?.id ?? null);
      setState('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const setActiveTab = useCallback(
    (next: SettingsDocumentsTab) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'types') params.delete('tab');
      else params.set('tab', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Keep the active selection in sync with the loaded types.
  useEffect(() => {
    if (activeTypeId == null && documentTypes.length > 0) {
      setActiveTypeId(documentTypes[0].id);
    }
  }, [activeTypeId, documentTypes]);

  const activeType = useMemo(
    () => documentTypes.find((t) => t.id === activeTypeId) ?? null,
    [documentTypes, activeTypeId]
  );

  const optionsByDefId = useMemo(() => {
    const map = new Map<string, PropertyOptionRow[]>();
    for (const opt of propertyOptions) {
      const list = map.get(opt.property_def_id);
      if (list) list.push(opt);
      else map.set(opt.property_def_id, [opt]);
    }
    return map;
  }, [propertyOptions]);

  const handleAddType = useCallback(async () => {
    if (!orgId) {
      setError('No workspace selected.');
      return;
    }
    const { data, error: rpcErr } = await supabase.rpc('create_document_type', {
      p_org_id: orgId,
      p_name: 'New type'
    });
    if (rpcErr) {
      setError(rpcErr.message);
      return;
    }
    const env = data as
      | { ok?: boolean; error?: string; type?: DocumentTypeRow }
      | null;
    if (!env || env.ok !== true || !env.type) {
      setError(env?.error ?? 'Could not create type.');
      return;
    }
    setDocumentTypes((prev) => [...prev, env.type as DocumentTypeRow]);
    setActiveTypeId(env.type.id);
  }, [orgId]);

  const handleArchiveType = useCallback(
    async (typeId: string) => {
      const target = documentTypes.find((t) => t.id === typeId);
      if (!target || target.built_in) return;
      const { data, error: rpcErr } = await supabase.rpc(
        'set_document_type_archived',
        { p_type_id: typeId, p_archived: true }
      );
      if (rpcErr) {
        setError(rpcErr.message);
        return;
      }
      const env = data as { ok?: boolean; error?: string } | null;
      if (env && env.ok === false) {
        setError(env.error ?? 'Could not archive type.');
        return;
      }
      setDocumentTypes((prev) => prev.filter((t) => t.id !== typeId));
      setActiveTypeId((cur) => (cur === typeId ? null : cur));
    },
    [documentTypes]
  );

  const handleAddProperty = useCallback(async () => {
    if (!orgId) {
      setError('No workspace selected.');
      return;
    }
    const label = 'New property';
    const takenKeys = new Set(propertyDefs.map((p) => p.key));
    const key = uniqueKey(propertyKey(label), takenKeys);
    const position =
      propertyDefs.reduce((max, p) => Math.max(max, p.position), -1) + 1;
    const { data, error: insErr } = await supabase
      .from('document_property_defs')
      .insert({
        org_id: orgId,
        type_id: null,
        key,
        kind: 'text',
        label,
        position,
        visible_in_list: false
      })
      .select('id, key, label, kind, position')
      .single();
    if (insErr || !data) {
      setError(insErr?.message ?? 'Could not create property');
      return;
    }
    // Legacy mirror so custom_attribute_values writes (which still FK to
    // custom_property_defs) resolve. defaultTypeId anchors the legacy NOT NULL
    // type_id; if no doc type exists, the modern row alone still drives the UI.
    if (defaultTypeId) {
      const { error: mirrorErr } = await supabase
        .from('custom_property_defs')
        .insert({
          id: data.id,
          org_id: orgId,
          type_id: defaultTypeId,
          key,
          kind: 'text',
          label,
          position,
          visible_in_list: false
        });
      if (mirrorErr) {
        setError(`Property created, legacy mirror failed: ${mirrorErr.message}`);
      }
    }
    setPropertyDefs((prev) => [...prev, data as PropertyDefRow]);
  }, [orgId, defaultTypeId, propertyDefs]);

  return (
    <section
      className="set-doc-page"
      data-testid="settings-documents"
      data-status={state}
      data-active-tab={activeTab}
    >
      <SettingsBackLink />

      <header className="set-doc-header" data-testid="settings-documents-header">
        <h1 className="set-doc-title">Document settings</h1>
        <MonoLabel size="sm" tracking="wide" tone="muted">
          Documents
        </MonoLabel>
      </header>
      <p className="set-doc-sub">
        Types and Property library for Documents. Types are the single Type field
        on every document; the Property library holds the custom property
        definitions.
      </p>

      <SettingsDocumentsTabs active={activeTab} onChange={setActiveTab} />

      {state === 'loading' ? (
        <div className="set-doc-loading">
          <Skeleton height={48} />
          <Skeleton height={320} />
        </div>
      ) : null}
      {state === 'unauth' ? (
        <Banner kind="default">Sign in to view document settings.</Banner>
      ) : null}
      {state === 'empty' ? (
        <Banner kind="default">You don't belong to any org yet.</Banner>
      ) : null}
      {state === 'error' ? (
        <Banner kind="error" data-testid="settings-documents-error">
          {error ?? "Couldn't load document settings. Try again."}
        </Banner>
      ) : null}

      {state === 'ready' && activeTab === 'types' ? (
        <div
          className="set-doc-pane set-doc-pane--subtypes"
          data-testid="settings-documents-pane-types"
        >
          <aside
            className="set-doc-list"
            data-testid="settings-documents-type-list"
          >
            {documentTypes.length === 0 ? (
              <p className="set-doc-empty">No types yet.</p>
            ) : (
              documentTypes.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="set-doc-list-item"
                  data-testid={`settings-documents-type-row-${t.id}`}
                  data-active={t.id === activeTypeId ? 'true' : 'false'}
                  onClick={() => setActiveTypeId(t.id)}
                >
                  <span className="set-doc-list-tag">
                    {t.name.slice(0, 4)}
                  </span>
                  <span className="set-doc-list-name">{t.name}</span>
                </button>
              ))
            )}
            <button
              type="button"
              className="set-doc-list-add"
              data-testid="settings-documents-add-type"
              onClick={() => void handleAddType()}
            >
              + New type
            </button>
          </aside>

          <div
            className="set-doc-panel"
            data-testid="settings-documents-type-panel"
            data-type-id={activeType?.id ?? ''}
          >
            {activeType ? (
              <>
                <h2
                  className="set-doc-panel-title"
                  data-testid="settings-documents-type-title"
                >
                  {activeType.name}
                  {activeType.built_in ? (
                    <Tag data-testid="settings-documents-type-builtin-badge">
                      Built-in
                    </Tag>
                  ) : null}
                </h2>
                <TextField
                  id={`settings-documents-type-${activeType.id}-name`}
                  label="Name"
                  value={activeType.name}
                  readOnly
                  data-testid="settings-documents-type-name"
                />
                <div className="set-doc-field">
                  <span className="set-doc-field-lbl">Slug</span>
                  <span
                    className="set-doc-slug"
                    data-testid="settings-documents-type-slug"
                  >
                    {activeType.key}
                  </span>
                </div>
                <div className="set-doc-field set-doc-field--block">
                  <span className="set-doc-field-lbl">Manage</span>
                  {activeType.built_in ? (
                    <p className="set-doc-empty">
                      Built-in types can't be archived.
                    </p>
                  ) : (
                    <Button
                      variant="danger"
                      size="sm"
                      data-testid="settings-documents-archive-type"
                      onClick={() => void handleArchiveType(activeType.id)}
                    >
                      Archive type
                    </Button>
                  )}
                </div>
                <p className="set-doc-legend">
                  Types are org-wide and shared by every member. Anyone who can
                  create documents can add a Type; archiving a user-created Type
                  hides it from the picker but leaves existing documents on it.
                </p>
              </>
            ) : (
              <p className="set-doc-empty">Add a type to get started.</p>
            )}
          </div>
        </div>
      ) : null}

      {state === 'ready' && activeTab === 'property-library' ? (
        <div
          className="set-doc-pane set-doc-pane--props"
          data-testid="settings-documents-pane-property-library"
        >
          <header className="set-doc-props-head">
            <h2 className="set-doc-props-title">Document property library</h2>
            <span className="set-doc-props-helper">
              Workspace-scoped · never mixes with the Work Item library
            </span>
            <button
              type="button"
              className="set-doc-props-new"
              data-testid="settings-documents-add-property"
              onClick={() => void handleAddProperty()}
            >
              + New property
            </button>
          </header>
          <table
            className="set-doc-props-table"
            data-testid="settings-documents-property-table"
          >
            <thead>
              <tr>
                <th className="set-doc-props-col-name">Name</th>
                <th>Type</th>
                <th>Options</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {propertyDefs.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="set-doc-empty"
                    data-testid="settings-documents-property-table-empty"
                  >
                    No properties yet. Click + New property to add one.
                  </td>
                </tr>
              ) : (
                propertyDefs.map((p) => {
                  const opts = optionsByDefId.get(p.id) ?? [];
                  const isSystem = SYSTEM_PROPERTY_KEYS.has(p.key);
                  const optionsCell =
                    p.kind === 'single_select' || p.kind === 'multi_select'
                      ? opts.length === 0
                        ? '—'
                        : opts.map((o) => o.label).join(' · ')
                      : '—';
                  return (
                    <tr
                      key={p.id}
                      data-testid={`settings-documents-property-row-${p.id}`}
                      data-system={isSystem ? '1' : '0'}
                    >
                      <td>
                        <span className="set-doc-props-name">
                          {p.label}
                          {isSystem ? (
                            <Tag
                              data-testid={`settings-documents-property-system-badge-${p.key}`}
                            >
                              System
                            </Tag>
                          ) : null}
                        </span>
                      </td>
                      <td>
                        <span className="set-doc-props-type">{p.kind}</span>
                      </td>
                      <td>{optionsCell}</td>
                      <td>
                        <button
                          type="button"
                          className="set-doc-props-edit"
                          data-testid={`settings-documents-property-edit-${p.id}`}
                          disabled
                          title={
                            isSystem
                              ? 'System property — label/kind locked'
                              : 'Editor lands in a later turn'
                          }
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <p
            className="set-doc-props-wall"
            data-testid="settings-documents-property-wall"
          >
            <strong>The wall.</strong> If your org wants <code>Tags</code> on
            both Documents AND Work Items, define it twice — once here, once in
            the Work Item property library. The wall keeps "is this a Document
            or a Work Item?" unambiguous.
          </p>
        </div>
      ) : null}
    </section>
  );
}
